import { forwardRef, useRef, useState } from 'react'
import cx from 'classnames'

import Styles from './SearchInput.module.scss'
import { filterDOMProps, mergeProps } from '@react-aria/utils'
import { useTextField, AriaTextFieldOptions } from '@react-aria/textfield'
import { Icon } from 'almanac-ui/icons/Types'
import { Close, Search } from 'almanac-ui/icons'
import { useLocalDebouncedFn } from 'hooks/useLocalDebouncedFn'
import { noop } from 'lodash'
import { Button } from 'components/Button'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useObjectRef } from 'utils/useObjectRef'

export interface SearchProps extends AriaTextFieldOptions<'input'> {
  // in milliseconds
  debounceInterval?: number
  noDebounce?: boolean
  className?: string
  wrapperClassName?: string
  focusedClassName?: string
  iconClassName?: string
  icon?: Icon
  variant?: 'default' | 'borderless'
  isParentHovered?: boolean
  keepIconInMobile?: boolean
  hideIcon?: boolean
  clearable?: boolean
  onClear?: () => void
  size?: 'md' | 'lg' | 'xl'
}

const SearchInput = forwardRef<HTMLInputElement, SearchProps>((props, fRef) => {
  const { icon: CustomIcon, variant = 'default', clearable, onClear = noop } = props
  const ref = useObjectRef<HTMLInputElement>(fRef)
  const onChange = (value: string) => (props.noDebounce ? props.onChange : debouncedOnChange)?.(value)
  const { inputProps } = useTextField({ ...props, onChange, 'aria-label': 'search' }, ref)

  const closeIconRef = useRef(null)
  const { isMobile } = useResponsiveDesign()

  const [focused, setFocused] = useState<boolean>(false)

  const { debounceInterval = 500, noDebounce, className, size = 'md', ...finalProps } = props

  const debouncedOnChange = useLocalDebouncedFn(
    (text: string) => {
      props.onChange?.(text)
    },
    debounceInterval,
    {},
    [props.onChange],
  )

  const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true)
    props.onFocus?.(event)
  }
  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (clearable && event.relatedTarget === closeIconRef.current) {
      onClear()
    } else {
      props.onBlur?.(event)
      setFocused(false)
    }
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      onClear()
    }
  }

  return (
    <div
      className={cx(Styles.wrapper, Styles[variant], props.wrapperClassName, Styles[size], {
        [Styles.focused]: focused,
        [props.focusedClassName ? props.focusedClassName : '']: focused,
        [Styles.isDisabled]: props.isDisabled,
        [Styles.hovered]: props.isParentHovered,
        [Styles.hideIcon]: props.hideIcon,
      })}
    >
      {!props.hideIcon && (!isMobile || props.keepIconInMobile) && (
        <div className={cx(Styles.icon, props.iconClassName)} role="img">
          {CustomIcon ? <CustomIcon /> : <Search />}
        </div>
      )}
      <input
        {...mergeProps(filterDOMProps(finalProps), inputProps)}
        className={cx(Styles.input, className)}
        ref={ref}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        role="search"
      />
      {clearable && typeof inputProps?.value == 'string' && inputProps?.value.length > 0 && focused && (
        <div className={Styles.clear}>
          {!isMobile && <kbd>ESC</kbd>}
          <Button variant="none" ref={closeIconRef} excludeFromTabOrder>
            <Close />
          </Button>
        </div>
      )}
    </div>
  )
})

export { SearchInput }
